import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, Button, Input, Select, Textarea, Field } from "@/components/UI";
import { Plus, Trash, Syringe, PencilSimple, Clock, Warning, CheckCircle, X, CalendarCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PerformerField } from "@/components/VetTable";

const EMPTY_DEF = { name: "", medicine_id: "", dose_per_animal: "", doses_per_cycle: 2, booster_interval_days: 21, repeat_period: "yıllık", repeat_custom_days: "", animal_type: "", notes: "" };
const PERIOD_LABELS = { "yıllık": "Yıllık", "6 aylık": "6 Aylık", "özel": "Özel" };

function today() { return new Date().toISOString().slice(0, 10); }

function UrgencyBadge({ s }) {
  if (s.days_until_due == null) return null;
  const d = s.days_until_due;
  const bg = d < 0 ? "#C45B45" : d <= 2 ? "#DDA74F" : "#8F9E93";
  const label = d < 0 ? `${Math.abs(d)} gün gecikti` : d === 0 ? "Bugün" : `${d} gün kaldı`;
  return <span className="text-[11px] px-2 py-0.5 rounded-full text-white font-semibold whitespace-nowrap tabnum" style={{ background: bg }}>{label}</span>;
}

function TaskChip({ s }) {
  const colors = { "ilk doz": "#417251", "rapel": "#C45B45", "periyodik": "#5A6E5F" };
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <span className="px-1.5 py-0.5 rounded text-white font-semibold" style={{ background: colors[s.task_type] || "#8F9E93" }}>{s.task_label}</span>
      <span className="tabnum text-[#6b746c]">Doz {s.dose_number}/{s.doses_per_cycle}</span>
    </span>
  );
}

export default function VaccineCalendar() {
  const { user } = useAuth();
  const canSeeCosts = !!user?.can_see_costs;
  const [defs, setDefs] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [apps, setApps] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [form, setForm] = useState(EMPTY_DEF);
  const [editingId, setEditingId] = useState(null);
  const [applyTarget, setApplyTarget] = useState(null);

  const load = () => {
    api.get("/vet/vaccines").then(r => setDefs(r.data)).catch(() => {});
    api.get("/vet/vaccines/schedules?status=bekliyor").then(r => setSchedules(r.data)).catch(() => {});
    api.get("/vet/vaccines/applications").then(r => setApps(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get(`/medicines?category=${encodeURIComponent("Aşı")}`).then(r => setMedicines(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch
  }, []);

  const submitDef = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Aşı adı girin");
    if (!form.medicine_id) return toast.error("Bağlı ilaç/aşı stok kalemi seçin");
    if (!(Number(form.dose_per_animal) > 0)) return toast.error("Doz miktarı girin");
    if (form.repeat_period === "özel" && !(Number(form.repeat_custom_days) > 0)) return toast.error("Özel periyot için gün sayısı girin");
    const payload = {
      name: form.name.trim(),
      medicine_id: form.medicine_id,
      dose_per_animal: Number(form.dose_per_animal),
      doses_per_cycle: Number(form.doses_per_cycle) || 1,
      booster_interval_days: Number(form.booster_interval_days) || 21,
      repeat_period: form.repeat_period,
      repeat_custom_days: form.repeat_period === "özel" ? Number(form.repeat_custom_days) : null,
      animal_type: form.animal_type || null,
      notes: form.notes || null,
    };
    try {
      if (editingId) {
        await api.patch(`/vet/vaccines/${editingId}`, payload);
        toast.success("Aşı tanımı güncellendi");
      } else {
        await api.post("/vet/vaccines", payload);
        toast.success("Aşı tanımı eklendi — ilk uygulamayı kaydettiğinizde rapel takvimi otomatik oluşur");
      }
      setForm(EMPTY_DEF); setEditingId(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Hata"); }
  };

  const editDef = (d) => {
    setEditingId(d.id);
    setForm({
      name: d.name || "", medicine_id: d.medicine_id || "",
      dose_per_animal: d.dose_per_animal ?? "", doses_per_cycle: d.doses_per_cycle ?? 1,
      booster_interval_days: d.booster_interval_days ?? 21,
      repeat_period: d.repeat_period || "yıllık", repeat_custom_days: d.repeat_custom_days ?? "",
      animal_type: d.animal_type || "", notes: d.notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const delDef = async (d) => {
    if (!window.confirm(`'${d.name}' aşı tanımı silinsin mi?\n\nBekleyen takvim görevleri de silinir. Uygulama geçmişi korunur.`)) return;
    try {
      await api.delete(`/vet/vaccines/${d.id}`);
      toast.success("Aşı tanımı silindi");
      if (editingId === d.id) { setEditingId(null); setForm(EMPTY_DEF); }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Hata"); }
  };

  const postpone = async (s) => {
    const nd = window.prompt("Yeni tarih (YYYY-AA-GG):", s.due_date);
    if (!nd) return;
    try {
      await api.patch(`/vet/vaccines/schedules/${s.id}`, { due_date: nd });
      toast.success(`Görev ${nd} tarihine ertelendi`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Hata"); }
  };

  const skip = async (s) => {
    if (!window.confirm(`'${s.vaccine_name}' görevi (Doz ${s.dose_number}/${s.doses_per_cycle}) atlansın mı?`)) return;
    try {
      await api.patch(`/vet/vaccines/schedules/${s.id}`, { status: "atlandı" });
      toast.success("Görev atlandı");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Hata"); }
  };

  const delApp = async (a) => {
    if (!window.confirm(`Bu aşı uygulaması geri alınsın mı?\n${a.date} · ${a.vaccine_name} · ${a.animal_count} baş\n\n${a.total_amount} ${a.medicine_unit || ""} stoğa iade edilir, finans gideri silinir ve bu uygulamanın oluşturduğu takvim görevi kaldırılır.`)) return;
    try {
      await api.delete(`/vet/vaccines/applications/${a.id}`);
      toast.success("Uygulama geri alındı — stok iade edildi");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Hata"); }
  };

  const openApply = (def, schedule = null) => setApplyTarget({ def, schedule });
  const dueSoon = schedules.filter(s => s.days_until_due != null && s.days_until_due <= 2);

  return (
    <div data-testid="vaccine-calendar" className="space-y-4">
      {dueSoon.length > 0 && (
        <div className="border-2 border-[#C45B45] bg-[#FBECEA] rounded-md p-4" data-testid="vaccine-alert-card">
          <div className="flex items-start gap-3">
            <Warning size={22} weight="fill" color="#C45B45" className="mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#8B2E1D] mb-1">💉 {dueSoon.length} aşı görevi yaklaşıyor veya gecikti</div>
              <div className="flex flex-wrap gap-2 mt-1">
                {dueSoon.slice(0, 8).map(s => (
                  <button key={s.id} onClick={() => { const d = defs.find(x => x.id === s.vaccine_def_id); if (d) openApply(d, s); }}
                    className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-md border border-[#E9B5AB] text-sm hover:bg-[#f7dfd9]"
                    data-testid={`vaccine-alert-item-${s.id}`}>
                    <b>{s.vaccine_name}</b>
                    <TaskChip s={s} />
                    <UrgencyBadge s={s} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <Card>
          <h3 className="heading text-lg mb-1">{editingId ? "Aşı Tanımını Düzenle" : "Yeni Aşı Tanımı"}</h3>
          <p className="text-xs text-[#8a938c] mb-3">İlk uygulama kaydedildiğinde rapel tarihleri, döngü tamamlanınca bir sonraki periyodik uygulama otomatik takvime işlenir.</p>
          <form onSubmit={submitDef} className="space-y-3">
            <Field label="Aşı Adı *">
              <Input data-testid="vaccine-form-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="örn. Şap Aşısı" />
            </Field>
            <Field label="Bağlı İlaç/Aşı Stok Kalemi *">
              <Select data-testid="vaccine-form-medicine" value={form.medicine_id} onChange={e => setForm({ ...form, medicine_id: e.target.value })}>
                <option value="">Seçin…</option>
                {medicines.map(m => <option key={m.id} value={m.id}>{m.name} ({m.stock} {m.usage_unit})</option>)}
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Doz / Hayvan *">
                <Input data-testid="vaccine-form-dose" type="number" step="0.1" min="0" value={form.dose_per_animal}
                  onChange={e => setForm({ ...form, dose_per_animal: e.target.value })} placeholder="örn. 2" />
              </Field>
              <Field label="Toplam Doz Sayısı (Döngü)">
                <Input data-testid="vaccine-form-doses-cycle" type="number" min="1" max="10" value={form.doses_per_cycle}
                  onChange={e => setForm({ ...form, doses_per_cycle: e.target.value })} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Rapel Süresi (Gün)">
                <Input data-testid="vaccine-form-booster" type="number" min="1" max="365" value={form.booster_interval_days}
                  onChange={e => setForm({ ...form, booster_interval_days: e.target.value })} />
              </Field>
              <Field label="Tekrarlama Periyodu">
                <Select data-testid="vaccine-form-period" value={form.repeat_period} onChange={e => setForm({ ...form, repeat_period: e.target.value })}>
                  <option value="yıllık">Yıllık (365 gün)</option>
                  <option value="6 aylık">6 Aylık (182 gün)</option>
                  <option value="özel">Özel (gün gir)</option>
                </Select>
              </Field>
            </div>
            {form.repeat_period === "özel" && (
              <Field label="Özel Periyot (Gün) *">
                <Input data-testid="vaccine-form-custom-days" type="number" min="1" value={form.repeat_custom_days}
                  onChange={e => setForm({ ...form, repeat_custom_days: e.target.value })} placeholder="örn. 90" />
              </Field>
            )}
            <Field label="Hedef Hayvan Grubu (Opsiyonel)">
              <Select data-testid="vaccine-form-animal" value={form.animal_type} onChange={e => setForm({ ...form, animal_type: e.target.value })}>
                <option value="">Tümü / Karma</option>
                <option value="buzağı">Buzağı</option>
                <option value="düve">Düve</option>
                <option value="inek">İnek</option>
                <option value="tosun">Tosun</option>
              </Select>
            </Field>
            <Field label="Notlar">
              <Textarea data-testid="vaccine-form-notes" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </Field>
            <div className="flex gap-2">
              <Button data-testid="vaccine-form-submit" type="submit" className="flex-1">
                <Plus size={16} className="inline mr-1" /> {editingId ? "Güncelle" : "Aşı Tanımı Ekle"}
              </Button>
              {editingId && (
                <Button data-testid="vaccine-form-cancel" type="button" variant="secondary" onClick={() => { setEditingId(null); setForm(EMPTY_DEF); }}>Vazgeç</Button>
              )}
            </div>
          </form>
        </Card>

        <Card className="lg:col-span-2" testId="vaccine-defs-card">
          <div className="flex items-center gap-2 mb-3">
            <Syringe size={20} weight="duotone" className="text-[#2D4A36]" />
            <h3 className="heading text-lg">Aşı Tanımları</h3>
          </div>
          {defs.length === 0 ? (
            <p className="text-sm text-[#8a938c]">Henüz aşı tanımı yok. Soldaki formdan ekleyin — ilk uygulamadan sonra takvim otomatik işler.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead><tr><th>Aşı</th><th>Bağlı Stok</th><th className="text-right">Doz/Baş</th><th>Döngü</th><th>Periyot</th><th>Sonraki Görev</th><th>İşlemler</th></tr></thead>
                <tbody>
                  {defs.map(d => (
                    <tr key={d.id} data-testid={`vaccine-def-row-${d.id}`}>
                      <td>
                        <div className="font-semibold">{d.name}</div>
                        {d.animal_type && <div className="text-[10px] text-[#8a938c]">{d.animal_type}</div>}
                      </td>
                      <td>
                        <div className="text-xs">{d.medicine_name}</div>
                        <div className="text-[10px] text-[#8a938c] tabnum">Stok: {d.medicine_stock} {d.medicine_unit}</div>
                      </td>
                      <td className="text-right tabnum">{d.dose_per_animal} {d.medicine_unit}</td>
                      <td className="text-xs tabnum">{d.doses_per_cycle} doz · {d.booster_interval_days}g ara</td>
                      <td className="text-xs">{PERIOD_LABELS[d.repeat_period] || d.repeat_period}{d.repeat_period === "özel" ? ` (${d.repeat_custom_days}g)` : ""}</td>
                      <td>
                        {d.next_schedule ? (
                          <div className="space-y-0.5">
                            <div className="text-xs tabnum">{d.next_schedule.due_date}</div>
                            <div className="flex items-center gap-1.5"><TaskChip s={d.next_schedule} /></div>
                            <UrgencyBadge s={d.next_schedule} />
                          </div>
                        ) : (
                          <span className="text-[11px] text-[#8a938c] italic">İlk uygulama bekleniyor</span>
                        )}
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          <button data-testid={`vaccine-apply-btn-${d.id}`} onClick={() => openApply(d, d.next_schedule)}
                            className="px-2 py-1 rounded text-[11px] font-semibold bg-[#2D4A36] text-white hover:bg-[#417251]">Uygula ✓</button>
                          <button data-testid={`vaccine-edit-btn-${d.id}`} onClick={() => editDef(d)}
                            className="px-2 py-1 rounded text-[11px] font-semibold border border-[#E5E2DC] hover:bg-[#F5F2EC]"><PencilSimple size={12} className="inline" /> Düzenle</button>
                          <button data-testid={`vaccine-del-btn-${d.id}`} onClick={() => delDef(d)}
                            className="px-2 py-1 rounded text-[11px] font-semibold border border-[#f2d4cb] text-[#C45B45] hover:bg-[#fbeee9]"><Trash size={12} className="inline" /> Sil</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Card testId="vaccine-schedule-card">
        <div className="flex items-center gap-2 mb-3">
          <CalendarCheck size={20} weight="duotone" className="text-[#2D4A36]" />
          <h3 className="heading text-lg">Aşı Takvimi — Bekleyen Görevler</h3>
          <span className="text-xs text-[#8a938c] ml-1">({schedules.length} görev)</span>
        </div>
        {schedules.length === 0 ? (
          <p className="text-sm text-[#8a938c]">Bekleyen görev yok. Bir aşının ilk uygulamasını kaydettiğinizde rapel ve periyodik görevler burada listelenir.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="vaccine-schedule-table">
              <thead><tr><th>Tarih</th><th>Aşı</th><th>Görev</th><th>Durum</th><th>İşlemler</th></tr></thead>
              <tbody>
                {schedules.map(s => (
                  <tr key={s.id} data-testid={`vaccine-schedule-row-${s.id}`} className={s.days_until_due != null && s.days_until_due < 0 ? "bg-[#fbeee9]" : ""}>
                    <td className="tabnum font-semibold">{s.due_date}</td>
                    <td>{s.vaccine_name}</td>
                    <td><TaskChip s={s} /></td>
                    <td><UrgencyBadge s={s} /></td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        <button data-testid={`vaccine-schedule-apply-${s.id}`}
                          onClick={() => { const d = defs.find(x => x.id === s.vaccine_def_id); if (d) openApply(d, s); else toast.error("Aşı tanımı bulunamadı"); }}
                          className="px-2 py-1 rounded text-[11px] font-semibold bg-[#2D4A36] text-white hover:bg-[#417251]"><CheckCircle size={12} className="inline" /> Uygula</button>
                        <button data-testid={`vaccine-schedule-postpone-${s.id}`} onClick={() => postpone(s)}
                          className="px-2 py-1 rounded text-[11px] font-semibold border border-[#E5E2DC] hover:bg-[#F5F2EC]"><Clock size={12} className="inline" /> Ertele</button>
                        <button data-testid={`vaccine-schedule-skip-${s.id}`} onClick={() => skip(s)}
                          className="px-2 py-1 rounded text-[11px] font-semibold border border-[#f2d4cb] text-[#C45B45] hover:bg-[#fbeee9]"><X size={12} className="inline" /> Atla</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card testId="vaccine-apps-card">
        <h3 className="heading text-lg mb-3">Uygulama Geçmişi</h3>
        {apps.length === 0 ? (
          <p className="text-sm text-[#8a938c]">Henüz aşı uygulaması kaydı yok.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="vaccine-apps-table">
              <thead><tr><th>Tarih</th><th>Aşı</th><th className="text-right">Hayvan</th><th>Doz</th><th className="text-right">Kullanılan</th>{canSeeCosts && <th className="text-right">Maliyet</th>}<th>Veteriner</th><th></th></tr></thead>
              <tbody>
                {apps.map(a => (
                  <tr key={a.id} data-testid={`vaccine-app-row-${a.id}`}>
                    <td className="tabnum">{a.date}</td>
                    <td>
                      <div className="font-semibold text-sm">{a.vaccine_name}</div>
                      {a.notes && <div className="text-[10px] text-[#8a938c]">{a.notes}</div>}
                    </td>
                    <td className="text-right tabnum">{a.animal_count} {a.animal_type || ""}</td>
                    <td className="text-xs tabnum">{a.dose_number}/{a.doses_per_cycle || "?"} <span className="text-[#8a938c]">(döngü {a.cycle_number})</span></td>
                    <td className="text-right tabnum">{a.total_amount} {a.medicine_unit}</td>
                    {canSeeCosts && <td className="text-right tabnum font-semibold">{a.cost != null ? `${Number(a.cost).toLocaleString("tr-TR")} ₺` : "—"}</td>}
                    <td className="text-xs">{a.vet_name || "—"}</td>
                    <td>
                      <button data-testid={`vaccine-app-del-${a.id}`} onClick={() => delApp(a)}
                        className="p-1.5 rounded hover:bg-[#fbeee9] text-[#C45B45]" title="Geri al (stok iade)"><Trash size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {applyTarget && (
        <ApplyModal target={applyTarget} canSeeCosts={canSeeCosts}
          onClose={() => setApplyTarget(null)}
          onDone={() => { setApplyTarget(null); load(); api.get("/medicines").then(r => setMedicines(r.data)).catch(() => {}); }} />
      )}
    </div>
  );
}

function ApplyModal({ target, onClose, onDone, canSeeCosts }) {
  const { user } = useAuth();
  const { def, schedule } = target;
  const [f, setF] = useState({ date: today(), animal_type: def.animal_type || "inek", animal_count: "", vet_name: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const total = Number(f.animal_count || 0) * Number(def.dose_per_animal || 0);
  const notEnough = total > Number(def.medicine_stock || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!(Number(f.animal_count) >= 1)) return toast.error("En az 1 hayvan girin");
    if (notEnough) return toast.error(`Yetersiz stok: ${def.medicine_name} — mevcut ${def.medicine_stock} ${def.medicine_unit}`);
    setSaving(true);
    try {
      const r = await api.post(`/vet/vaccines/${def.id}/apply`, {
        date: f.date, animal_type: f.animal_type, animal_count: Number(f.animal_count),
        schedule_id: schedule?.id || null, vet_name: f.vet_name?.trim() || user?.name || null, notes: f.notes || null,
      });
      const d = r.data;
      toast.success(d.cycle_completed
        ? `Aşı döngüsü tamamlandı ✓ Sonraki periyodik uygulama takvime eklendi: ${d.next_schedule?.due_date}`
        : `Aşı uygulandı ✓ Rapel tarihi takvime eklendi: ${d.next_schedule?.due_date}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Hata");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="vaccine-apply-modal" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="heading text-lg">💉 Aşı Uygula · {def.name}</h3>
          <button data-testid="vaccine-apply-close" onClick={onClose} className="p-1 rounded hover:bg-[#F5F2EC]"><X size={18} /></button>
        </div>
        {schedule ? (
          <div className="mb-3 text-xs bg-[#eaf1ec] border border-[#c9d5cc] rounded p-2.5 text-[#2D4A36]">
            📅 Takvim görevi: <b>{schedule.task_label}</b> · Doz {schedule.dose_number}/{schedule.doses_per_cycle} · Planlanan: <b>{schedule.due_date}</b>
          </div>
        ) : (
          <div className="mb-3 text-xs bg-[#fdf5e5] border border-[#f0d9a3] rounded p-2.5 text-[#7a5a10]">
            🆕 İlk uygulama — kayıttan sonra rapel tarihi otomatik hesaplanıp takvime eklenecek.
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tarih *">
              <Input data-testid="vaccine-apply-date" type="date" value={f.date} onChange={e => setF({ ...f, date: e.target.value })} />
            </Field>
            <Field label="Hayvan Sayısı *">
              <Input data-testid="vaccine-apply-count" type="number" min="1" value={f.animal_count} autoFocus
                onChange={e => setF({ ...f, animal_count: e.target.value })} placeholder="örn. 120" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Hayvan Tipi">
              <Select data-testid="vaccine-apply-animal" value={f.animal_type} onChange={e => setF({ ...f, animal_type: e.target.value })}>
                <option value="buzağı">Buzağı</option>
                <option value="düve">Düve</option>
                <option value="inek">İnek</option>
                <option value="tosun">Tosun</option>
              </Select>
            </Field>
            <PerformerField label="Veteriner / Uygulayan (İşlemi Yapan)" user={user} value={f.vet_name}
              onChange={v => setF({ ...f, vet_name: v })} testId="vaccine-apply-vet" />
          </div>
          <Field label="Notlar">
            <Textarea data-testid="vaccine-apply-notes" rows={2} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} />
          </Field>
          {total > 0 && (
            <div className={`text-sm rounded p-2.5 border tabnum ${notEnough ? "bg-[#fbeee9] border-[#f2d4cb] text-[#8a3a2b]" : "bg-[#eaf1ec] border-[#c9d5cc] text-[#2D4A36]"}`}
              data-testid="vaccine-apply-preview">
              💊 {f.animal_count} baş × {def.dose_per_animal} {def.medicine_unit} = <b>{Number(total.toFixed(3))} {def.medicine_unit}</b> stoktan düşülecek
              <div className="text-[11px] mt-0.5">{def.medicine_name} mevcut stok: {def.medicine_stock} {def.medicine_unit}{notEnough && " — YETERSİZ!"}</div>
            </div>
          )}
          <Button data-testid="vaccine-apply-submit" type="submit" className="w-full" disabled={saving || notEnough}>
            {saving ? "Kaydediliyor…" : "Uygulamayı Kaydet"}
          </Button>
        </form>
      </div>
    </div>
  );
}
